/**
 * AxLabs Mathematics Experiments - Graphs and Networks
 *
 * Collection of experiments exploring graph theory and networks:
 * connectivity, paths, trees, coloring, and network flow.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// DEGREE DISTRIBUTION
// ============================================================================
export const DegreeDistribution = createExperimentFromSchema({
    id: 'degree-distribution',
    name: 'Degree Distribution',
    domain: 'mathematics',
    description: 'Analyze degree distribution in graphs',
    conceptTags: ['Degree Distribution', 'Graph Properties', 'Network Analysis', 'Degree Sequence'],
    difficulty: 4,
    stateVariables: [
        { id: 'avgDegree', name: 'Average Degree', unit: 'dimensionless', initial: 2, symbol: '\\bar{d}' },
        { id: 'maxDegree', name: 'Max Degree', unit: 'dimensionless', initial: 4, symbol: 'd_{max}' },
        { id: 'minDegree', name: 'Min Degree', unit: 'dimensionless', initial: 1, symbol: 'd_{min}' },
        { id: 'degreeSum', name: 'Sum of Degrees', unit: 'dimensionless', initial: 8, symbol: '\\sum d_i' },
        { id: 'handshaking', name: '2|E|', unit: 'dimensionless', initial: 8, symbol: '2|E|' },
    ],
    parameters: [
        { id: 'graphType', name: 'Graph Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'G' },
        { id: 'n', name: 'Vertices n', unit: 'dimensionless', min: 3, max: 10, default: 4, symbol: 'n' },
    ],
    equations: [
        {
            id: 'handshaking-lemma',
            name: 'Handshaking Lemma',
            latex: '\\sum_{v \\in V} d(v) = 2|E|',
            description: 'Sum of degrees equals twice the number of edges',
        },
        {
            id: 'avg-degree',
            name: 'Average Degree',
            latex: '\\bar{d} = \\frac{2|E|}{n}',
            description: 'Average degree relates to edges and vertices',
        },
    ],
    derivatives: (_state, params) => {
        const graphType = Math.round(getParam(params, 'graphType'));
        const n = Math.round(getParam(params, 'n'));
        // Define some sample graphs
        let avgDegree = 0;
        let maxDegree = 0;
        let minDegree = 0;
        let degreeSum = 0;
        let edges = 0;
        switch (graphType) {
            case 0: // Complete graph Kₙ
                edges = n * (n - 1) / 2;
                degreeSum = n * (n - 1);
                avgDegree = n - 1;
                maxDegree = n - 1;
                minDegree = n - 1;
                break;
            case 1: // Cycle Cₙ
                edges = n;
                degreeSum = 2 * n;
                avgDegree = 2;
                maxDegree = 2;
                minDegree = 2;
                break;
            case 2: // Path Pₙ
                edges = n - 1;
                degreeSum = 2 * (n - 1);
                avgDegree = 2 * (n - 1) / n;
                maxDegree = 2;
                minDegree = 1;
                break;
            case 3: // Star graph
                edges = n - 1;
                degreeSum = 2 * (n - 1);
                avgDegree = 2 * (n - 1) / n;
                maxDegree = n - 1;
                minDegree = 1;
                break;
        }
        return {
            avgDegree: avgDegree - getState(_state, 'avgDegree'),
            maxDegree: maxDegree - getState(_state, 'maxDegree'),
            minDegree: minDegree - getState(_state, 'minDegree'),
            degreeSum: degreeSum - getState(_state, 'degreeSum'),
            handshaking: 2 * edges - getState(_state, 'handshaking'),
        };
    },
    bounds: {
        avgDegree: { min: 0, max: 20 },
        maxDegree: { min: 0, max: 20 },
    },
});
// ============================================================================
// TREE PROPERTIES
// ============================================================================
export const TreeProperties = createExperimentFromSchema({
    id: 'tree-properties',
    name: 'Tree Properties',
    domain: 'mathematics',
    description: 'Explore properties of trees',
    conceptTags: ['Trees', 'Spanning Trees', 'Forest', 'Acyclic Graphs'],
    difficulty: 4,
    stateVariables: [
        { id: 'n', name: 'Vertices n', unit: 'dimensionless', initial: 5, symbol: 'n' },
        { id: 'edges', name: 'Edges |E|', unit: 'dimensionless', initial: 4, symbol: '|E|' },
        { id: 'isTree', name: 'Is Tree', unit: 'dimensionless', initial: 1, symbol: 'T' },
        { id: 'leaves', name: 'Leaf Count', unit: 'dimensionless', initial: 2, symbol: 'L' },
        { id: 'height', name: 'Height', unit: 'dimensionless', initial: 3, symbol: 'h' },
    ],
    parameters: [
        { id: 'nTarget', name: 'Vertices n', unit: 'dimensionless', min: 2, max: 15, default: 5, symbol: 'n' },
        { id: 'treeType', name: 'Tree Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'Type' },
    ],
    equations: [
        {
            id: 'tree-property',
            name: 'Tree Characterization',
            latex: '|E| = |V| - 1 \\text{ and connected}',
            description: 'Tree is connected with n-1 edges',
        },
        {
            id: 'spanning-tree',
            name: 'Spanning Tree',
            latex: '\\text{Every connected graph has a spanning tree}',
            description: 'Subgraph that is a tree containing all vertices',
        },
    ],
    derivatives: (_state, params) => {
        const nTarget = Math.round(getParam(params, 'nTarget'));
        const treeType = Math.round(getParam(params, 'treeType'));
        const edges = nTarget - 1;
        const isTree = 1;
        let leaves = 0;
        let height = 0;
        switch (treeType) {
            case 0: // Path graph
                leaves = 2;
                height = nTarget - 1;
                break;
            case 1: // Star graph
                leaves = nTarget - 1;
                height = 2;
                break;
            case 2: // Balanced binary tree
                leaves = Math.pow(2, Math.floor(Math.log2(nTarget + 1)));
                height = Math.ceil(Math.log2(nTarget + 1));
                break;
        }
        return {
            n: nTarget - getState(_state, 'n'),
            edges: edges - getState(_state, 'edges'),
            isTree: isTree - getState(_state, 'isTree'),
            leaves: leaves - getState(_state, 'leaves'),
            height: height - getState(_state, 'height'),
        };
    },
    bounds: {
        n: { min: 2, max: 20 },
        edges: { min: 1, max: 20 },
        leaves: { min: 1, max: 20 },
    },
});
// ============================================================================
// CYCLE DETECTION
// ============================================================================
export const CycleDetection = createExperimentFromSchema({
    id: 'cycle-detection',
    name: 'Cycle Detection',
    domain: 'mathematics',
    description: 'Detect cycles in graphs and data structures',
    conceptTags: ['Cycles', 'Acyclic', 'DFS', 'Graph Algorithms'],
    difficulty: 5,
    stateVariables: [
        { id: 'nodes', name: 'Nodes Visited', unit: 'dimensionless', initial: 1, symbol: 'v' },
        { id: 'hasCycle', name: 'Has Cycle', unit: 'dimensionless', initial: 0, symbol: 'C' },
        { id: 'cycleLength', name: 'Cycle Length', unit: 'dimensionless', initial: 0, symbol: '|C|' },
        { id: 'backEdge', name: 'Back Edge Found', unit: 'dimensionless', initial: 0, symbol: 'BE' },
    ],
    parameters: [
        { id: 'graphPattern', name: 'Graph Pattern', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'G' },
    ],
    equations: [
        {
            id: 'acyclic-condition',
            name: 'Acyclic Condition',
            latex: '\\text{No cycle } \\iff \\text{ DFS finds no back edge}',
            description: 'Graph is acyclic if DFS traversal finds no back edge',
        },
        {
            id: 'tree-acyclic',
            name: 'Tree Property',
            latex: '\\text{Tree } \\iff \\text{ connected and acyclic}',
            description: 'Trees are connected acyclic graphs',
        },
    ],
    derivatives: (state, params) => {
        const graphPattern = Math.round(getParam(params, 'graphPattern'));
        const patterns = [
            { hasCycle: 0, cycleLength: 0 }, // Tree (no cycle)
            { hasCycle: 1, cycleLength: 3 }, // Triangle (3-cycle)
            { hasCycle: 1, cycleLength: 4 }, // Square (4-cycle)
            { hasCycle: 1, cycleLength: 5 }, // Pentagon (5-cycle)
        ];
        const pattern = patterns[graphPattern] ?? patterns[0];
        const backEdge = pattern?.hasCycle ?? 0;
        return {
            nodes: 1,
            hasCycle: (pattern?.hasCycle ?? 0) - getState(state, 'hasCycle'),
            cycleLength: (pattern?.cycleLength ?? 0) - getState(state, 'cycleLength'),
            backEdge: backEdge - getState(state, 'backEdge'),
        };
    },
    bounds: {
        cycleLength: { min: 0, max: 10 },
    },
});
// ============================================================================
// BIPARTITE GRAPH TESTING
// ============================================================================
export const BipartiteGraphTesting = createExperimentFromSchema({
    id: 'bipartite-graph-testing',
    name: 'Bipartite Graph Testing',
    domain: 'mathematics',
    description: 'Test if graphs are bipartite (2-colorable)',
    conceptTags: ['Bipartite Graphs', 'Graph Coloring', '2-Colorability', 'Matching'],
    difficulty: 5,
    stateVariables: [
        { id: 'n', name: 'Vertices n', unit: 'dimensionless', initial: 4, symbol: 'n' },
        { id: 'isBipartite', name: 'Is Bipartite', unit: 'dimensionless', initial: 1, symbol: 'B' },
        { id: 'partition1', name: 'Partition |U|', unit: 'dimensionless', initial: 2, symbol: '|U|' },
        { id: 'partition2', name: 'Partition |V|', unit: 'dimensionless', initial: 2, symbol: '|V|' },
        { id: 'oddCycle', name: 'Has Odd Cycle', unit: 'dimensionless', initial: 0, symbol: 'C_{odd}' },
    ],
    parameters: [
        { id: 'graphType', name: 'Graph Type', unit: 'dimensionless', min: 0, max: 3, default: 0, symbol: 'G' },
    ],
    equations: [
        {
            id: 'bipartite-def',
            name: 'Bipartite Definition',
            latex: 'V = U \\cup V, \\text{ no edge within U or within V}',
            description: 'Vertices can be partitioned into two independent sets',
        },
        {
            id: 'bipartite-theorem',
            name: 'König\'s Theorem',
            latex: 'G \\text{ is bipartite } \\iff G \\text{ has no odd cycle}',
            description: 'Graph is bipartite iff it contains no odd cycle',
        },
    ],
    derivatives: (_state, params) => {
        const graphType = Math.round(getParam(params, 'graphType'));
        let isBipartite = 1;
        let partition1 = 2;
        let partition2 = 2;
        let oddCycle = 0;
        switch (graphType) {
            case 0: // Complete bipartite K₂,₂
                isBipartite = 1;
                partition1 = 2;
                partition2 = 2;
                oddCycle = 0;
                break;
            case 1: // Triangle (K₃)
                isBipartite = 0;
                partition1 = 1;
                partition2 = 2;
                oddCycle = 1;
                break;
            case 2: // Square (C₄)
                isBipartite = 1;
                partition1 = 2;
                partition2 = 2;
                oddCycle = 0;
                break;
            case 3: // Pentagon (C₅)
                isBipartite = 0;
                partition1 = 2;
                partition2 = 3;
                oddCycle = 1;
                break;
        }
        return {
            n: 4,
            isBipartite: isBipartite - getState(_state, 'isBipartite'),
            partition1: partition1 - getState(_state, 'partition1'),
            partition2: partition2 - getState(_state, 'partition2'),
            oddCycle: oddCycle - getState(_state, 'oddCycle'),
        };
    },
    bounds: {
        partition1: { min: 0, max: 10 },
        partition2: { min: 0, max: 10 },
    },
});
// ============================================================================
// NETWORK FLOW CAPACITY
// ============================================================================
export const NetworkFlowCapacity = createExperimentFromSchema({
    id: 'network-flow-capacity',
    name: 'Network Flow Capacity',
    domain: 'mathematics',
    description: 'Explore max flow and min cut in networks',
    conceptTags: ['Network Flow', 'Max Flow Min Cut', 'Ford-Fulkerson', 'Capacity'],
    difficulty: 6,
    stateVariables: [
        { id: 'flow', name: 'Current Flow', unit: 'dimensionless', initial: 5, symbol: '|f|' },
        { id: 'capacity', name: 'Min Cut Capacity', unit: 'dimensionless', initial: 5, symbol: 'c(S,T)' },
        { id: 'source', name: 'Source s', unit: 'dimensionless', initial: 0, symbol: 's' },
        { id: 'sink', name: 'Sink t', unit: 'dimensionless', initial: 3, symbol: 't' },
        { id: 'maxFlow', name: 'Max Flow', unit: 'dimensionless', initial: 5, symbol: '|f|_{max}' },
    ],
    parameters: [
        { id: 'networkType', name: 'Network Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'N' },
    ],
    equations: [
        {
            id: 'max-flow-min-cut',
            name: 'Max-Flow Min-Cut Theorem',
            latex: '|f|_{max} = c(S,T)',
            description: 'Maximum flow equals minimum cut capacity',
        },
        {
            id: 'flow-conservation',
            name: 'Flow Conservation',
            latex: '\\sum_{v \\in V} f(u,v) = \\sum_{v \\in V} f(v,u)',
            description: 'Flow in equals flow out (except source/sink)',
        },
    ],
    derivatives: (_state, params) => {
        const networkType = Math.round(getParam(params, 'networkType'));
        // Sample networks
        const networks = [
            { flow: 5, capacity: 5 }, // Simple: source -> 5 -> sink
            { flow: 3, capacity: 3 }, // Parallel paths: 2+3 split
            { flow: 6, capacity: 6 }, // Complex: multiple paths
        ];
        const network = networks[networkType] ?? networks[0];
        return {
            flow: (network?.flow ?? 0) - getState(_state, 'flow'),
            capacity: (network?.capacity ?? 0) - getState(_state, 'capacity'),
            source: 0,
            sink: 3,
            maxFlow: (network?.capacity ?? 0) - getState(_state, 'maxFlow'),
        };
    },
    bounds: {
        flow: { min: 0, max: 20 },
        capacity: { min: 0, max: 20 },
    },
});
// ============================================================================
// GRAPH COLORING MINIMUM
// ============================================================================
export const GraphColoringMinimum = createExperimentFromSchema({
    id: 'graph-coloring-minimum',
    name: 'Graph Coloring Minimum',
    domain: 'mathematics',
    description: 'Find minimum chromatic number of graphs',
    conceptTags: ['Graph Coloring', 'Chromatic Number', 'Four Color Theorem', 'Planar Graphs'],
    difficulty: 6,
    stateVariables: [
        { id: 'n', name: 'Vertices n', unit: 'dimensionless', initial: 4, symbol: 'n' },
        { id: 'chi', name: 'Chromatic Number χ', unit: 'dimensionless', initial: 2, symbol: '\\chi(G)' },
        { id: 'planar', name: 'Is Planar', unit: 'dimensionless', initial: 1, symbol: 'P' },
        { id: 'fourColor', name: '4-Colorable', unit: 'dimensionless', initial: 1, symbol: '4C' },
    ],
    parameters: [
        { id: 'graphType', name: 'Graph Type', unit: 'dimensionless', min: 0, max: 4, default: 0, symbol: 'G' },
    ],
    equations: [
        {
            id: 'chromatic-number',
            name: 'Chromatic Number',
            latex: '\\chi(G) = \\min\\{k : G \\text{ is } k\\text{-colorable}\\}',
            description: 'Minimum colors needed for proper coloring',
        },
        {
            id: 'four-color-theorem',
            name: 'Four Color Theorem',
            latex: 'G \\text{ planar } \\implies \\chi(G) \\leq 4',
            description: 'Any planar graph can be colored with 4 colors',
        },
    ],
    derivatives: (_state, params) => {
        const graphType = Math.round(getParam(params, 'graphType'));
        let chi = 2;
        let planar = 1;
        let fourColor = 1;
        const n = 4;
        switch (graphType) {
            case 0: // Bipartite graph
                chi = 2;
                planar = 1;
                fourColor = 1;
                break;
            case 1: // Complete graph K₄
                chi = 4;
                planar = 1;
                fourColor = 1;
                break;
            case 2: // Complete graph K₅
                chi = 5;
                planar = 0;
                fourColor = 0;
                break;
            case 3: // Cycle C₄
                chi = 2;
                planar = 1;
                fourColor = 1;
                break;
            case 4: // Odd cycle C₅
                chi = 3;
                planar = 1;
                fourColor = 1;
                break;
        }
        return {
            n,
            chi: chi - getState(_state, 'chi'),
            planar: planar - getState(_state, 'planar'),
            fourColor: fourColor - getState(_state, 'fourColor'),
        };
    },
    bounds: {
        chi: { min: 1, max: 10 },
    },
});
// ============================================================================
// ADD MISSING GRAPH EXPERIMENTS
// ============================================================================
export const GraphConnectivity = createExperimentFromSchema({
    id: 'graph-connectivity',
    name: 'Graph Connectivity',
    domain: 'mathematics',
    description: 'Explore graph connectivity and connected components',
    conceptTags: ['Graph Theory', 'Connectivity', 'Paths', 'Adjacency Matrix'],
    difficulty: 5,
    stateVariables: [
        { id: 'edges', name: 'Edge Count', unit: 'dimensionless', initial: 3, symbol: '|E|' },
        { id: 'vertices', name: 'Vertex Count', unit: 'dimensionless', initial: 3, symbol: '|V|' },
        { id: 'connected', name: 'Is Connected', unit: 'dimensionless', initial: 1, symbol: 'C' },
        { id: 'components', name: 'Components', unit: 'dimensionless', initial: 1, symbol: 'k' },
        { id: 'minEdges', name: 'Min Edges for Connected', unit: 'dimensionless', initial: 2, symbol: 'n-1' },
    ],
    parameters: [
        { id: 'edge1', name: 'Edge 1-2', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: 'e_{12}' },
        { id: 'edge2', name: 'Edge 2-3', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: 'e_{23}' },
        { id: 'edge3', name: 'Edge 1-3', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: 'e_{13}' },
        { id: 'n', name: 'Vertices n', unit: 'dimensionless', min: 3, max: 6, default: 3, symbol: 'n' },
    ],
    equations: [
        {
            id: 'connected-condition',
            name: 'Connected Graph',
            latex: '\\text{Connected if path between every pair of vertices}',
            description: 'Graph is connected if no isolated vertices',
        },
        {
            id: 'min-edges-connected',
            name: 'Minimum Edges for Connected',
            latex: '|E| \\geq n-1',
            description: 'At least n-1 edges needed for connectivity',
        },
    ],
    derivatives: (_state, params) => {
        const e1 = getParam(params, 'edge1');
        const e2 = getParam(params, 'edge2');
        const e3 = getParam(params, 'edge3');
        const n = Math.round(getParam(params, 'n'));
        const edges = (e1 + e2 + e3);
        const vertices = n;
        // Check if graph is connected (simplified for n=3)
        let connected = 0;
        let components = n;
        if (n === 3) {
            if (e1 > 0.5 && e2 > 0.5) {
                connected = 1;
                components = 1;
            }
            else if (e1 > 0.5 && e3 > 0.5) {
                connected = 1;
                components = 1;
            }
            else if (e2 > 0.5 && e3 > 0.5) {
                connected = 1;
                components = 1;
            }
            else if (e1 > 0.5 || e2 > 0.5 || e3 > 0.5) {
                components = 2;
            }
            else {
                components = 3;
            }
        }
        const minEdges = n - 1;
        return {
            edges: edges - getState(_state, 'edges'),
            vertices: vertices - getState(_state, 'vertices'),
            connected: connected - getState(_state, 'connected'),
            components: components - getState(_state, 'components'),
            minEdges: minEdges - getState(_state, 'minEdges'),
        };
    },
    bounds: {
        edges: { min: 0, max: 10 },
        vertices: { min: 3, max: 6 },
        components: { min: 1, max: 6 },
    },
});
export const ShortestPathFinding = createExperimentFromSchema({
    id: 'shortest-path-finding',
    name: 'Shortest Path Finding',
    domain: 'mathematics',
    description: 'Find shortest paths in weighted graphs using Dijkstra/BFS',
    conceptTags: ['Shortest Path', 'Dijkstra', 'Weighted Graphs', 'Path Length'],
    difficulty: 5,
    stateVariables: [
        { id: 'source', name: 'Source Node', unit: 'dimensionless', initial: 0, symbol: 's' },
        { id: 'target', name: 'Target Node', unit: 'dimensionless', initial: 3, symbol: 't' },
        { id: 'pathLength', name: 'Path Length', unit: 'dimensionless', initial: 5, symbol: 'd(s,t)' },
        { id: 'intermediate', name: 'Via Node', unit: 'dimensionless', initial: 1, symbol: 'via' },
        { id: 'hops', name: 'Number of Hops', unit: 'dimensionless', initial: 2, symbol: 'hops' },
    ],
    parameters: [
        { id: 'graphType', name: 'Graph Type', unit: 'dimensionless', min: 0, max: 2, default: 0, symbol: 'G' },
    ],
    equations: [
        {
            id: 'shortest-path-def',
            name: 'Shortest Path',
            latex: 'd(s,t) = \\min\\{\\text{length of paths from s to t}\\}',
            description: 'Minimum weight path between nodes',
        },
        {
            id: 'triangle-inequality',
            name: 'Triangle Inequality',
            latex: 'd(i,j) \\leq d(i,k) + d(k,j)',
            description: 'Direct path is no longer than path through intermediate',
        },
    ],
    derivatives: (_state, params) => {
        const graphType = Math.round(getParam(params, 'graphType'));
        // Simple 4-node graphs
        const graphs = [
            // Graph 0: Linear 0-1-2-3 with weights 2,3,4
            { adj: [[0, 2, 999, 999], [2, 0, 3, 999], [999, 3, 0, 4], [999, 999, 4, 0]], path: 9, via: [1, 2], hops: 3 },
            // Graph 1: 0-1-3 (2+2=4), 0-2-3 (3+3=6), 0-3 direct (5)
            { adj: [[0, 2, 3, 5], [2, 0, 999, 2], [3, 999, 0, 3], [5, 2, 3, 0]], path: 4, via: [1], hops: 2 },
            // Graph 2: Star with center 0
            { adj: [[0, 1, 1, 1], [1, 0, 999, 999], [1, 999, 0, 999], [1, 999, 999, 0]], path: 2, via: [0], hops: 2 },
        ];
        const graph = graphs[graphType] ?? graphs[0];
        const pathLength = graph?.path ?? 0;
        const hops = graph?.hops ?? 0;
        return {
            source: 0,
            target: 3,
            pathLength: pathLength - getState(_state, 'pathLength'),
            intermediate: ((graph?.via[0]) ?? 0) - getState(_state, 'intermediate'),
            hops: hops - getState(_state, 'hops'),
        };
    },
    bounds: {
        pathLength: { min: 0, max: 20 },
        hops: { min: 1, max: 5 },
    },
});
export const EulerPathExistence = createExperimentFromSchema({
    id: 'euler-path-existence',
    name: 'Euler Path Existence',
    domain: 'mathematics',
    description: 'Determine existence of Euler paths and circuits by degree conditions',
    conceptTags: ['Euler Path', 'Euler Circuit', 'Degree', 'Traversability'],
    difficulty: 5,
    stateVariables: [
        { id: 'oddDegree', name: 'Odd Degree Count', unit: 'dimensionless', initial: 2, symbol: '|V_{odd}|' },
        { id: 'hasPath', name: 'Euler Path', unit: 'dimensionless', initial: 1, symbol: 'EP' },
        { id: 'hasCircuit', name: 'Euler Circuit', unit: 'dimensionless', initial: 0, symbol: 'EC' },
        { id: 'traversable', name: 'Traversable', unit: 'dimensionless', initial: 1, symbol: 'T' },
    ],
    parameters: [
        { id: 'd1', name: 'Degree v₁', unit: 'dimensionless', min: 0, max: 5, default: 2, symbol: 'd(v_1)' },
        { id: 'd2', name: 'Degree v₂', unit: 'dimensionless', min: 0, max: 5, default: 2, symbol: 'd(v_2)' },
        { id: 'd3', name: 'Degree v₃', unit: 'dimensionless', min: 0, max: 5, default: 3, symbol: 'd(v_3)' },
        { id: 'd4', name: 'Degree v₄', unit: 'dimensionless', min: 0, max: 5, default: 3, symbol: 'd(v_4)' },
    ],
    equations: [
        {
            id: 'euler-circuit',
            name: 'Euler Circuit Condition',
            latex: '\\text{All vertices have even degree}',
            description: 'Euler circuit exists iff all vertices have even degree',
        },
        {
            id: 'euler-path',
            name: 'Euler Path Condition',
            latex: 'exactly\\ 0\\ or\\ 2\\ vertices\\ have\\ odd\\ degree',
            description: 'Euler path exists iff 0 or 2 vertices have odd degree',
        },
    ],
    derivatives: (_state, params) => {
        const d1 = Math.round(getParam(params, 'd1'));
        const d2 = Math.round(getParam(params, 'd2'));
        const d3 = Math.round(getParam(params, 'd3'));
        const d4 = Math.round(getParam(params, 'd4'));
        const degrees = [d1, d2, d3, d4];
        const oddDegree = degrees.filter(d => d % 2 === 1).length;
        // Euler circuit: all even degrees
        const hasCircuit = oddDegree === 0 ? 1 : 0;
        // Euler path: exactly 0 or 2 odd degrees (connected graph assumed)
        const hasPath = (oddDegree === 0 || oddDegree === 2) ? 1 : 0;
        // Traversable if has path
        const traversable = hasPath;
        return {
            oddDegree: oddDegree - getState(_state, 'oddDegree'),
            hasPath: hasPath - getState(_state, 'hasPath'),
            hasCircuit: hasCircuit - getState(_state, 'hasCircuit'),
            traversable: traversable - getState(_state, 'traversable'),
        };
    },
    bounds: {
        oddDegree: { min: 0, max: 4 },
    },
});
// ============================================================================
// EXPORT ALL GRAPHS AND NETWORKS EXPERIMENTS
// ============================================================================
export const ALL_GRAPHS = [
    DegreeDistribution,
    TreeProperties,
    CycleDetection,
    BipartiteGraphTesting,
    NetworkFlowCapacity,
    GraphColoringMinimum,
    GraphConnectivity,
    ShortestPathFinding,
    EulerPathExistence,
];
//# sourceMappingURL=graphs-networks.js.map